import { Drawer, message } from 'antd';
import BigNumber from 'bignumber.js';
import { Check, ChevronDown, Copy, XIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ellipseAddress } from 'src/helpers';
import { desktopAddressKey, removeAuthTokens } from 'src/helpers/storage';
import { useCopy } from 'src/hooks/useCopy';
import { useAuth } from 'src/libs/rainbow';
import { queryClient } from 'src/libs/react-query';
import { userKeys } from 'src/services/user/user-keys';
import { emojiAvatarForAddress } from 'src/utils/emojiAvatarForAddress';
import { Address } from 'viem';
import { useBalance } from 'wagmi';
import { useAppTranslation } from 'src/i18n/hooks';
import './linked-desktop-wallet.scss';
import { useAppDispatch } from 'src/store';
import { clearUserInfo } from 'src/store/actions/user';

const LinkedDesktopWallet = () => {
  const userTheme = useSelector((state: any) => state.theme);
  const { t } = useAppTranslation(['common']);
  const [copyApiBlocked, setCopyApiBlocked] = useState(false);

  const { setAuthStatus } = useAuth();
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false);
  const walletAddress = localStorage.getItem(desktopAddressKey);
  const { copy, copied } = useCopy();

  const { emoji, color } = useMemo(
    () => emojiAvatarForAddress(walletAddress ?? ''),
    [walletAddress],
  );

  const { data } = useBalance({
    address: walletAddress as Address,
  });

  const handleCopyAddress = () => {
    if (!copyApiBlocked) {
      copy(walletAddress);
      return;
    }
    message.error(t('wallet.copyApiBlockedWarning'));
  };

  const handleDisconnect = async () => {
    removeAuthTokens();
    setAuthStatus('unauthenticated');
    dispatch(clearUserInfo())
    await queryClient.resetQueries({
      queryKey: userKeys.all,
    });
  };

  const handleCheckCopyApi = async () => {
    try {
      const result = await navigator.permissions.query({
        name: 'clipboard-write' as PermissionName,
      });
      if (result.state === 'denied') {
        setCopyApiBlocked(true);
      }
    } catch {
      setCopyApiBlocked(true);
    }
  };

  useEffect(() => {
    handleCheckCopyApi();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <div>
      <div
        onClick={() => setOpen(true)}
        className="flex gap-[4px] items-center bg-white rounded-[12px] shadow-[0_4px_12px_rgba(0,0,0,0.1)] px-[8px] py-[6px] dark:bg-[#1A1B1F] dark:border-[1px] dark:border-[#1A1B1F] h-[40px] hover:scale-[1.025] transition"
      >
        <div
          style={{
            backgroundColor: color,
          }}
          className="flex justify-center items-center w-[24px] h-[24px] rounded-[100px]"
        >
          {emoji}
        </div>
        <span className="font-[700] leading-[26px]">{ellipseAddress(walletAddress ?? '')}</span>
        <ChevronDown size={14} className="dark:text-white" />
      </div>
      <Drawer
        id="wallet-information"
        className={userTheme}
        placement={'bottom'}
        closable={false}
        onClose={handleClose}
        open={open}
        height={277}
      >
        <div className="relative">
          <div
            onClick={handleClose}
            className="absolute right-[0] top-[0] rounded-[100px] bg-[#0000000f] p-[5px] text-[#3c4242cc]"
          >
            <XIcon size={20} />
          </div>
        </div>
        <div className="flex justify-center item-center">
          <div
            style={{
              backgroundColor: color,
            }}
            className="flex justify-center items-center w-[82px] h-[82px] rounded-[100px] text-[45px] mt-[30px]"
          >
            {emoji}
          </div>
        </div>
        <p className="text-[16px] font-[700] text-center mt-[20px] dark:text-white">
          {copyApiBlocked ? walletAddress : ellipseAddress(walletAddress ?? '')}
        </p>
        <p className=" text-center text-[16px] font-[600] text-[#3c424299] dark:text-[#fff9]">
          {BigNumber(data?.formatted ?? '0').toFixed(2)} {data?.symbol}
        </p>
        <div className="flex gap-[10px] mt-[10px]">
          <div
            onClick={handleCopyAddress}
            className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a]"
          >
            {copied ? (
              <Check className="dark:text-white" size={18} />
            ) : (
              <Copy className="dark:text-white" size={18} />
            )}
            <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
              {copied ? `${t('wallet.copied')}!` : t('wallet.copyAddress')}
            </span>
          </div>
          <div
            onClick={handleDisconnect}
            className="flex flex-col flex-1 items-center justify-center bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] p-[6px] rounded-[12px] hover:scale-[1.025] transition dark:bg-[#e0e8ff1a]"
          >
            <svg
              className="dark:text-white"
              fill="none"
              height="16"
              viewBox="0 0 18 16"
              width="18"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.67834 15.5908H9.99963C11.5514 15.5908 12.399 14.7432 12.399 13.1777V10.2656H10.6354V12.9863C10.6354 13.5332 10.3688 13.8271 9.78772 13.8271H2.89026C2.3092 13.8271 2.0426 13.5332 2.0426 12.9863V3.15625C2.0426 2.60254 2.3092 2.30859 2.89026 2.30859H9.78772C10.3688 2.30859 10.6354 2.60254 10.6354 3.15625V5.89746H12.399V2.95801C12.399 1.39941 11.5514 0.544922 9.99963 0.544922H2.67834C1.12659 0.544922 0.278931 1.39941 0.278931 2.95801V13.1777C0.278931 14.7432 1.12659 15.5908 2.67834 15.5908ZM7.43616 8.85059H14.0875L15.0924 8.78906L14.566 9.14453L13.6842 9.96484C13.5406 10.1016 13.4586 10.2861 13.4586 10.4844C13.4586 10.8398 13.7321 11.168 14.1217 11.168C14.3199 11.168 14.4635 11.0928 14.6002 10.9561L16.7809 8.68652C16.986 8.48145 17.0543 8.27637 17.0543 8.06445C17.0543 7.85254 16.986 7.64746 16.7809 7.43555L14.6002 5.17285C14.4635 5.03613 14.3199 4.9541 14.1217 4.9541C13.7321 4.9541 13.4586 5.27539 13.4586 5.6377C13.4586 5.83594 13.5406 6.02734 13.6842 6.15723L14.566 6.98438L15.0924 7.33984L14.0875 7.27148H7.43616C7.01917 7.27148 6.65686 7.62012 6.65686 8.06445C6.65686 8.50195 7.01917 8.85059 7.43616 8.85059Z"
                fill="currentColor"
              ></path>
            </svg>
            <span className="text-[12px] text-[#25292E] font-[600] dark:text-white">
              {t('wallet.disconnect')}
            </span>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default LinkedDesktopWallet;
